/**
 * Where the signed-in account's check stands, read from `useAuth`.
 *
 * The auth provider starts every session with the student role so the route
 * guards have something to evaluate, and a failed first check keeps that
 * default rather than inventing another. Nothing on screen may treat the
 * default as the account's role: a teacher whose check failed would read
 * "Student", see the student tabs and land on the student home. Only a status
 * the server sent confirms the role; the provider's own neutral 'unknown'
 * never does, and a failed refetch keeps the last confirmed values.
 *
 * - `checking`: nothing confirmed yet (signing in, or no check has finished).
 * - `failed`: the check failed before anything was confirmed; offer Try again.
 * - `inactive`: confirmed, but the account isn't activated yet.
 * - `active`: confirmed and active; the role label and the tabs may show.
 */

export type AccountState = 'checking' | 'failed' | 'inactive' | 'active';

export interface AccountSignals {
  isRoleLoaded: boolean;
  userStatus: string;
  authError: string | null;
}

export function accountStateOf({
  isRoleLoaded,
  userStatus,
  authError,
}: AccountSignals): AccountState {
  if (!isRoleLoaded) return 'checking';
  if (userStatus === 'active') return 'active';
  if (userStatus === 'inactive') return 'inactive';
  return authError ? 'failed' : 'checking';
}

/** What the account chip says in place of a role before activation. */
export const NOT_ACTIVATED_LABEL = 'Not activated yet';
